import { ErrorDeLaApi, pedir } from './nucleo'
import type { Usuario } from './tipos'

/**
 * La cuenta de cada persona: recuperar la contraseña, ponerla con el enlace del correo,
 * cambiarla (o el correo) pidiendo la de ahora, y mandar el enlace a alguien de la cuenta.
 * Recuperar y poner son los de Payload (`forgot-password`, `reset-password`), con el correo
 * y los mensajes del núcleo; lo demás, del panel (`/api/panel/…`).
 */

/** Lo que se le dice a quien no ha podido entrar: el bloqueo por intentos no es «contraseña incorrecta». */
export const mensajeDeEntrada = (fallo: unknown): null | string => {
  if (!fallo) return null
  if (fallo instanceof ErrorDeLaApi) {
    if (fallo.datos.codigo === 'cuenta-bloqueada') return fallo.message
    if (fallo.status === 401) return 'El correo o la contraseña no son correctos.'
    return fallo.message
  }
  return 'No hemos podido conectar. Comprueba tu conexión y vuelve a intentarlo.'
}

/**
 * El enlace del correo lleva el token en el fragmento (`#token=…&invitacion=1`), que no llega
 * al servidor ni se queda en su registro. Se acepta también en la consulta, por si algún
 * programa de correo se come el fragmento.
 */
export const leerEnlace = (hash: string, busqueda: string): { invitacion: boolean; token: null | string } => {
  const del = (texto: string) => new URLSearchParams(texto.replace(/^[#?]/, ''))
  const fragmento = del(hash)
  const consulta = del(busqueda)
  const token = fragmento.get('token') ?? consulta.get('token')
  return {
    token: token && /^[a-f0-9]{16,128}$/i.test(token) ? token : null,
    invitacion: (fragmento.get('invitacion') ?? consulta.get('invitacion')) === '1',
  }
}

export const cuenta = {
  /** Siempre responde lo mismo, exista o no el correo: no dice quién tiene cuenta. */
  pedirEnlace: (email: string) => pedir('/usuarios/forgot-password', { metodo: 'POST', cuerpo: { email }, toleraAnonimo: true }),
  /** Pone la contraseña y deja la sesión iniciada (la cookie la pone el núcleo). */
  ponerContrasena: (token: string, password: string) =>
    pedir<{ user: Usuario }>('/usuarios/reset-password?depth=1', { metodo: 'POST', cuerpo: { token, password }, toleraAnonimo: true }),
  cambiarCredenciales: (datos: { actual: string; email?: string; nueva?: string }) => pedir<{ mensaje: string }>('/panel/cuenta/credenciales', { metodo: 'POST', cuerpo: datos }),
  cambiarNombre: (id: number, nombre: string) => pedir<{ doc: Usuario }>(`/usuarios/${id}`, { metodo: 'PATCH', cuerpo: { nombre } }),
  mandarEnlace: (usuario: number) => pedir<{ mensaje: string }>('/panel/usuarios/enlace', { metodo: 'POST', cuerpo: { usuario } }),
  /** Deshacer una invitación que nadie ha aceptado: si solo era de esta cuenta, la persona se borra. */
  revocar: (usuario: number, cliente: number) => pedir<{ mensaje: string; borrado: boolean }>('/panel/usuarios/revocar', { metodo: 'POST', cuerpo: { usuario, cliente } }),
}

/** El mensaje de un campo concreto que viene del núcleo (`{ campo, errors: [{ path, message }] }`). */
export const errorDelCampo = (fallo: unknown, campo: string): string | undefined => {
  if (!(fallo instanceof ErrorDeLaApi)) return undefined
  if (fallo.datos.campo === campo) return fallo.message
  const lista = Array.isArray(fallo.datos.errors) ? (fallo.datos.errors as Array<{ path?: string; message?: string }>) : []
  return lista.find((e) => e.path === campo)?.message
}

export const MINIMO_DE_CONTRASENA = 10
